import { createSelector } from 'reselect';
import { fromUnixTime, startOfWeek, format } from 'date-fns';
import _groupBy from 'lodash/groupBy';
import { selectActiveVenueId, selectRouteParams, selectRouteQuery } from 'selectors/general';
import {
  reviewsData,
  reviewsStatistics,
  ratingsInstructors,
  classesRatings,
  instructorsRatings,
  ratingsClasses,
} from 'store/saga/entities/bound';
import { initialState } from './Ratings.reducers';
import { ALL_USERS } from './RatingsUsersFilter/RatingsUsersFilter.helpers';

const ratingsSelector = state => state.ratings;

export const selectHasMore = createSelector(ratingsSelector, ratings => ratings.hasMore);

export const selectCurrentPage = createSelector(ratingsSelector, ratings => ratings.currentPage);

export const selectRatingsClasses = createSelector(
  state => state || {},
  selectActiveVenueId,
  (state, venueId) => (ratingsClasses.loaded(state, venueId) ? ratingsClasses.data(state, venueId) : [])
);

export const selectUsersFilterKey = createSelector(ratingsSelector, ratings => ratings.selectedUsersFilterKey);

export const selectSelectedClassId = createSelector(ratingsSelector, ratings => ratings.selectedClassId);

export const selectSelectedClass = createSelector(
  selectRatingsClasses,
  selectSelectedClassId,
  (classesList, selectedId) => classesList.find(({ id }) => id === selectedId) || {}
);

export const selectRatingsInstructors = createSelector(
  state => state || {},
  selectActiveVenueId,
  (state, venueId) => (ratingsInstructors.loaded(state, venueId) ? ratingsInstructors.data(state, venueId) : [])
);

export const selectSelectedInstructorId = createSelector(ratingsSelector, ratings => ratings.selectedInstructorId);

export const selectSelectedInstructor = createSelector(
  state => state || {},
  selectSelectedInstructorId,
  (state, selectedId) => {
    const instrucorList = selectRatingsInstructors(state);
    return instrucorList.find(({ id }) => id === selectedId) || {};
  }
);

export const selectActiveFilters = createSelector(
  selectSelectedClassId,
  selectSelectedInstructorId,
  selectUsersFilterKey,
  (classId, instructorId, userFilterKey) => ({
    classId,
    instructorId,
    userFilterKey,
  })
);

export const selectClassesRatingsIsLoading = createSelector(
  state => state || {},
  selectActiveVenueId,
  selectSelectedInstructorId,
  (state, venueId, instructorId) => {
    const key = classesRatings.defaultKey(null, venueId, instructorId);
    return classesRatings.loading(state, key);
  }
);

export const selectClassesRatings = createSelector(
  state => state || {},
  selectActiveVenueId,
  selectSelectedInstructorId,
  (state, venueId, instructorId) => {
    const key = classesRatings.defaultKey(null, venueId, instructorId);
    return (classesRatings.loaded(state, key) && classesRatings.data(state, key)) || [];
  }
);

export const selectInstructorsRatingsIsLoading = createSelector(
  state => state || {},
  selectActiveVenueId,
  selectSelectedClassId,
  (state, venueId, classId) => {
    const key = instructorsRatings.defaultKey(null, venueId, classId);
    return instructorsRatings.loading(state, key);
  }
);

export const selectInstructorsRatings = createSelector(
  state => state || {},
  selectActiveVenueId,
  selectSelectedClassId,
  (state, venueId, classId) => {
    const key = instructorsRatings.defaultKey(null, venueId, classId);
    return (instructorsRatings.loaded(state, key) && instructorsRatings.data(state, key)) || [];
  }
);

export const isAnyFilterSelected = createSelector(
  selectSelectedClassId,
  selectSelectedInstructorId,
  selectUsersFilterKey,
  (classId, instructorId, userFilterKey) => !!(classId || instructorId || userFilterKey !== ALL_USERS)
);

export const selectShouldRefreshRatingsList = createSelector(
  ratingsSelector,
  ratings => ratings.shouldRefreshRatingsList
);

export const selectSelectedTab = createSelector(ratingsSelector, ratings => ratings.selectedTab || 0);

export const selectRouteQueries = createSelector(selectRouteParams, selectRouteQuery, (params, query) => {
  const { c, i, v } = query;

  return {
    tab: params.tab || initialState.selectedTab,
    classId: Number(c) || initialState.selectedClassId,
    instructorId: Number(i) || initialState.selectedInstructorId,
    userFilterKey: v || initialState.selectedUsersFilterKey,
  };
});

export const isReviewsLoading = createSelector(
  state => state || {},
  selectActiveVenueId,
  selectCurrentPage,
  selectActiveFilters,
  (state, venueId, pageNumber, filters) => {
    const key = reviewsData.defaultKey(null, venueId, pageNumber, filters);
    return reviewsData.loading(state, key);
  }
);

export const selectReviews = createSelector(
  state => state || {},
  selectActiveVenueId,
  selectCurrentPage,
  selectActiveFilters,
  (state, venueId, pageNumber, filters) => {
    const key = reviewsData.defaultKey(null, venueId, pageNumber, filters);
    return reviewsData.loaded(state, key) && reviewsData.data(state, key).reviews
      ? reviewsData.data(state, key).reviews
      : [];
  }
);

export const selectReviewsGroupedByWeek = createSelector(selectReviews, reviews =>
  _groupBy(reviews, ({ createdOn }) => format(startOfWeek(fromUnixTime(createdOn)), 'yyyy-MM-dd'))
);

export const selectIsReviewsStatisticsLoading = createSelector(
  state => state || {},
  selectActiveVenueId,
  selectActiveFilters,
  (state, venueId, filters) => {
    const key = reviewsStatistics.defaultKey(null, venueId, filters);
    return reviewsStatistics.loading(state, key);
  }
);

export const selectReviewsStatistics = createSelector(
  state => state || {},
  selectActiveVenueId,
  selectActiveFilters,
  (state, venueId, filters) => {
    const key = reviewsStatistics.defaultKey(null, venueId, filters);
    return reviewsStatistics.loaded(state, key) ? reviewsStatistics.data(state, key) : {};
  }
);
